/**
 * Look up a company page through the server so the browser never scrapes
 * directly (CORS + private-host rules live there).
 *
 *   POST /api/morrow/company-brief { url }
 *     -> { website, title, brief, situation } or { error }
 */

import { apiUrl } from "./apiBase";

export type MorrowCompanySituation = "marketing" | "product" | "sales" | "general";

export type MorrowCompanyBrief = {
  readonly website: string;
  readonly title: string;
  readonly brief: string;
  readonly situation: MorrowCompanySituation;
};

const SITUATIONS: ReadonlySet<string> = new Set(["marketing", "product", "sales", "general"]);

export function parseCompanySituation(value: unknown): MorrowCompanySituation | "" {
  return typeof value === "string" && SITUATIONS.has(value)
    ? (value as MorrowCompanySituation)
    : "";
}

export function parseCompanyBriefPayload(value: unknown): MorrowCompanyBrief | null {
  if (!value || typeof value !== "object") return null;
  const rec = value as Record<string, unknown>;
  const brief = typeof rec.brief === "string" ? rec.brief.trim() : "";
  const situation = parseCompanySituation(rec.situation);
  if (!brief || !situation) return null;
  return {
    website: typeof rec.website === "string" ? rec.website.trim() : "",
    title: typeof rec.title === "string" ? rec.title.trim() : "",
    brief,
    situation,
  };
}

export async function fetchCompanyBrief(url: string): Promise<MorrowCompanyBrief | null> {
  const trimmed = url.trim();
  if (!trimmed) return null;
  try {
    const res = await fetch(apiUrl("/api/morrow/company-brief"), {
      method: "POST",
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify({ url: trimmed }),
    });
    if (!res.ok) return null;
    return parseCompanyBriefPayload(await res.json());
  } catch {
    return null;
  }
}
